import React, { useState } from "react";
import { SingleQuestion } from "./Question";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const Questions = ({
  questions,
  selectedAnswers,
  onAnswerChange,
  isReview,
  correctAnswers,
  trackQUestionClick,
  result = [],
  startIndex = 1,
  courseId,
  sectionId,
  userId,
  isManagerReview = false,
  onMarksChange,
  overriddenMarks = {},
  questionTimeTracking,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const handleSave = (question) => {
    const original = question.point || 1;
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed < 0) {
      setEditingId(null);
      return;
    }
    // Cap at original marks
    const capped = Math.min(parsed, original);
    
    // Find the original score to see if it was actually changed
    const questionResult = Array.isArray(result)
      ? result.find(
          (r) =>
            String(r.questionId) === String(question.id) ||
            String(r.questionId) === String(question.tempId)
        )
      : result && (String(result.questionId) === String(question.id) || String(result.questionId) === String(question.tempId))
      ? result
      : undefined;
      
    const originalScore = questionResult?.final_score !== undefined 
      ? questionResult.final_score 
      : (questionResult?.status === "correct" 
          ? (question.point || 1) 
          : (questionResult?.status === "incorrect" ? 0 : (question.point || 1)));

    if (capped === Number(originalScore)) {
      if (onMarksChange) onMarksChange(question.id, null);
    } else {
      if (onMarksChange) onMarksChange(question.id, capped);
    }
    
    setEditingId(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue("");
  };

  return (
    <>
      {questions.map((question, index) => {
        const questionResult = Array.isArray(result)
          ? result.find(
              (r) =>
                String(r.questionId) === String(question.id) ||
                String(r.questionId) === String(question.tempId)
            )
          : result && (String(result.questionId) === String(question.id) || String(result.questionId) === String(question.tempId))
          ? result
          : undefined;

        // Display marks obtained by student, fallback to original point if no result
        const displayMarks = overriddenMarks[question.id] ?? (questionResult?.final_score !== undefined 
          ? questionResult.final_score 
          : (questionResult?.status === "correct" 
              ? (question.point || 1) 
              : (questionResult?.status === "incorrect" ? 0 : (question.point || 1))));
        const isEditing = editingId === question.id;

        const handleEditClickLocal = () => {
          setEditingId(question.id);
          setEditValue(String(displayMarks));
        };

        return (
          <div key={question.id} className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold">Question {startIndex + index}</span>
              {(
                <span className="d-flex align-items-center gap-2">
                  {isEditing ? (
                    <div className="d-flex align-items-center gap-1 bg-white p-1 border rounded shadow-sm">
                      <button
                        className="btn btn-sm btn-link p-0 text-primary"
                        onClick={() => {
                          const val = parseFloat(editValue) || 0;
                          if (val > 0) setEditValue(String(Math.max(0, val - 0.5)));
                        }}
                        title="Decrease"
                      >
                        <span style={{ fontSize: '1.2rem', fontWeight: 'bold', padding: '0 5px' }}>-</span>
                      </button>
                      <input
                        type="number"
                        min="0"
                        max={question.point || 1}
                        step="0.5"
                        value={editValue}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || (parseFloat(val) >= 0 && parseFloat(val) <= (question.point || 1))) {
                            setEditValue(val);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSave(question);
                          if (e.key === "Escape") handleCancel();
                        }}
                        autoFocus
                        style={{
                          width: "60px",
                          padding: "2px 4px",
                          fontSize: "0.85rem",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          textAlign: "center",
                          outline: "none",
                        }}
                      />
                      <button
                        className="btn btn-sm btn-link p-0 text-primary"
                        onClick={() => {
                          const val = parseFloat(editValue) || 0;
                          if (val < (question.point || 1)) setEditValue(String(Math.min((question.point || 1), val + 0.5)));
                        }}
                        title="Increase"
                      >
                        <span style={{ fontSize: '1.2rem', fontWeight: 'bold', padding: '0 5px' }}>+</span>
                      </button>
                      <span
                        style={{ fontSize: "0.75rem", color: "#6c757d", borderLeft: "1px solid #dee2e6", paddingLeft: "5px", marginLeft: "2px" }}
                      >
                        / {question.point || 1}
                      </span>
                      <CheckIcon
                        onClick={() => handleSave(question)}
                        sx={{
                          fontSize: 18,
                          color: "#28a745",
                          cursor: "pointer",
                          ml: 1
                        }}
                        titleAccess="Save"
                      />
                      <CloseIcon
                        onClick={handleCancel}
                        sx={{
                          fontSize: 18,
                          color: "#dc3545",
                          cursor: "pointer",
                        }}
                        titleAccess="Cancel"
                      />
                    </div>
                  ) : (
                    <>
                      {(!isReview || isManagerReview) && (
                        <span
                          className="text-muted fw-bold small"
                          style={{
                            color:
                              overriddenMarks[question.id] !== undefined
                                ? "#224B99"
                                : undefined,
                          }}
                        >
                          Marks : {displayMarks}
                          {isReview && (
                            <span
                              style={{
                                fontSize: "0.7rem",
                                marginLeft: "4px",
                                color: "#6c757d",
                              }}
                            >
                              (of {question.point || 1})
                            </span>
                          )}
                        </span>
                      )}
                      {isReview && questionTimeTracking && (
                        <span
                          className="small"
                          style={{
                            color: questionTimeTracking[String(question.id)] !== undefined ? "#224B99" : "#adb5bd",
                            background: "#f0f4f8",
                            borderRadius: "10px",
                            padding: "2px 8px",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                          }}
                          title="Time spent on this question"
                        >
                          ⏱ {questionTimeTracking[String(question.id)] !== undefined
                            ? (questionTimeTracking[String(question.id)] < 60
                                ? `${Math.round(questionTimeTracking[String(question.id)])}s`
                                : `${Math.floor(questionTimeTracking[String(question.id)] / 60)}m ${Math.round(questionTimeTracking[String(question.id)] % 60)}s`)
                            : "skipped"}
                        </span>
                      )}
                      {isManagerReview && (
                        <EditIcon
                          onClick={handleEditClickLocal}
                          sx={{
                            fontSize: 15,
                            color: "#6c757d",
                            cursor: "pointer",
                            "&:hover": { color: "#224B99" },
                          }}
                          titleAccess="Edit marks"
                        />
                      )}
                    </>
                  )}
                </span>
              )}
            </div>
            <SingleQuestion
              item={question}
              selectedAnswer={selectedAnswers[question.id]}
              onAnswerChange={(value) =>
                !isReview && onAnswerChange(question.id, value)
              }
              isReview={isReview}
              correctAnswers={correctAnswers}
              trackQUestionClick={trackQUestionClick}
              result={questionResult}
              courseId={courseId}
              sectionId={sectionId}
              userId={userId}
            />

            {isReview && Array.isArray(question.rubric) && question.rubric.length > 0 && (
              <div className="mt-2 p-3 rounded" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
                <div className="fw-bold small text-uppercase mb-2" style={{ color: "#224B99" }}>
                  Rubric Evaluation
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: "0.8rem" }}>
                    <thead className="table-light">
                      <tr>
                        <th>Criterion</th>
                        <th>Description</th>
                        <th style={{ width: "90px" }}>Marks</th>
                        {questionResult?.evaluation?.rubric_scores && <th>Evaluator's Reason</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {question.rubric.map((r, ridx) => {
                        const scored = questionResult?.evaluation?.rubric_scores?.find(
                          (s) => s.criterion === r.criterion
                        );
                        return (
                          <tr key={ridx}>
                            <td className="fw-semibold">{r.criterion}</td>
                            <td>{r.description}</td>
                            <td>
                              {scored
                                ? `${scored.obtained_marks} / ${scored.max_marks}`
                                : `— / ${r.marks}`}
                            </td>
                            {questionResult?.evaluation?.rubric_scores && (
                              <td className="text-muted">{scored?.reason || "—"}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {questionResult?.evaluation?.overall_score !== undefined && (
                  <div className="text-end small fw-bold mt-2" style={{ color: "#224B99" }}>
                    Total: {questionResult.evaluation.overall_score} / {questionResult.evaluation.total_marks}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default Questions;
